import { ThemedText } from "@/components/themed-text";
import { BrandColors, Colors } from "@/constants/theme";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
  useColorScheme,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

interface AuthScreenLayoutProps {
  onBack: () => void;
  children: React.ReactNode;
  keyboardInset?: boolean;
  showBackButton?: boolean;
}

export function AuthScreenLayout({
  onBack,
  children,
  keyboardInset = false,
  showBackButton = true,
}: AuthScreenLayoutProps) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  return (
    <SafeAreaView
      style={[authScreenStyles.safeArea, { backgroundColor: theme.background }]}
      edges={["top", "bottom"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={authScreenStyles.container}
      >
        <ScrollView
          contentContainerStyle={authScreenStyles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          {...(keyboardInset
            ? {
                automaticallyAdjustKeyboardInsets: true,
                keyboardDismissMode: "interactive" as const,
              }
            : {})}
        >
          <View style={authScreenStyles.topBar}>
            {showBackButton && (
              <TouchableOpacity
                onPress={onBack}
                style={[
                  authScreenStyles.circleButton,
                  { borderColor: theme.border },
                ]}
                accessibilityRole="button"
                accessibilityLabel="Go back"
              >
                <Ionicons
                  name="chevron-back"
                  size={20}
                  color={BrandColors.charcoal}
                />
              </TouchableOpacity>
            )}
          </View>
          {children}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

interface AuthScreenContentProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

export function AuthScreenContent({ children, style }: AuthScreenContentProps) {
  return <View style={[authScreenStyles.content, style]}>{children}</View>;
}

interface AuthScreenHeaderProps {
  title: string;
  subtitle: string;
}

export function AuthScreenHeader({ title, subtitle }: AuthScreenHeaderProps) {
  return (
    <View style={authScreenStyles.header}>
      <ThemedText type="title" style={authScreenStyles.title}>
        {title}
      </ThemedText>
      <ThemedText style={authScreenStyles.subtitle}>{subtitle}</ThemedText>
    </View>
  );
}

export function AuthScreenSpacer() {
  return <View style={authScreenStyles.spacer} />;
}

interface AuthScreenFooterProps {
  children: React.ReactNode;
}

export function AuthScreenFooter({ children }: AuthScreenFooterProps) {
  return <View style={authScreenStyles.footer}>{children}</View>;
}

interface AuthDesktopLayoutProps {
  onBack?: () => void;
  children: React.ReactNode;
}

export function AuthDesktopLayout({
  onBack,
  children,
}: AuthDesktopLayoutProps) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  return (
    <ScrollView
      style={{ backgroundColor: theme.background }}
      contentContainerStyle={[
        authScreenStyles.desktopPage,
        { backgroundColor: theme.card },
      ]}
      keyboardShouldPersistTaps="handled"
    >
      <View style={authScreenStyles.desktopTopBar}>
        {onBack && (
          <TouchableOpacity
            onPress={onBack}
            style={authScreenStyles.desktopBackButton}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons
              name="chevron-back"
              size={14}
              color={BrandColors.mediumGray}
            />
            <ThemedText style={authScreenStyles.desktopBackText}>
              Back
            </ThemedText>
          </TouchableOpacity>
        )}
      </View>
      <View style={authScreenStyles.desktopColumn}>{children}</View>
    </ScrollView>
  );
}

export function AuthDesktopHeader({ title, subtitle }: AuthScreenHeaderProps) {
  return (
    <View style={authScreenStyles.desktopHeader}>
      <ThemedText style={authScreenStyles.desktopTitle}>{title}</ThemedText>
      <ThemedText style={authScreenStyles.desktopSubtitle}>
        {subtitle}
      </ThemedText>
    </View>
  );
}

export function AuthDesktopCard({ children, style }: AuthScreenContentProps) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  return (
    <View
      style={[
        authScreenStyles.desktopCard,
        { backgroundColor: theme.background, borderColor: theme.border },
        style,
      ]}
    >
      {children}
    </View>
  );
}

export const authScreenStyles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: BrandColors.white,
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 32,
    width: "100%",
    maxWidth: 700,
    marginHorizontal: "auto",
    shadowColor: BrandColors.mediumGray,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 8,
  },
  circleButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BrandColors.white,
  },
  content: {
    paddingHorizontal: 24,
  },
  header: {
    gap: 8,
    marginBottom: 28,
  },
  title: {
    fontWeight: "700",
  },
  subtitle: {
    color: BrandColors.mediumGray,
    fontSize: 16,
  },
  spacer: {
    flexGrow: 1,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
  },
  footerText: {
    fontSize: 14,
  },
  link: {
    fontSize: 14,
    color: BrandColors.primary,
    fontWeight: "600",
  },
  form: {
    gap: 20,
  },
  field: {
    gap: 8,
  },
  input: {
    fontSize: 16,
  },
  inputOutline: {
    borderRadius: 12,
  },
  continueButton: {
    borderRadius: 16,
    marginTop: 4,
    paddingVertical: 6,
    ...Platform.select({
      ios: {
        shadowColor: BrandColors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  continueButtonLabel: {
    fontSize: 16,
    fontWeight: "600",
  },
  continueButtonContent: {
    flexDirection: "row-reverse",
    gap: 8,
  },
  desktopPage: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingBottom: 48,
  },
  desktopTopBar: {
    height: 48,
    justifyContent: "center",
    paddingHorizontal: 48,
  },
  desktopBackButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 4,
  },
  desktopBackText: {
    fontSize: 13,
    lineHeight: 18,
    color: BrandColors.mediumGray,
  },
  desktopColumn: {
    width: "100%",
    maxWidth: 400,
    alignSelf: "center",
  },
  desktopHeader: {
    alignItems: "center",
    gap: 4,
    marginTop: 12,
    marginBottom: 20,
  },
  desktopTitle: {
    fontSize: 28,
    lineHeight: 36,
    fontWeight: "500",
  },
  desktopSubtitle: {
    fontSize: 13,
    lineHeight: 18,
    color: BrandColors.mediumGray,
  },
  desktopCard: {
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 16,
  },
  desktopForm: {
    gap: 12,
  },
  desktopInput: {
    height: 40,
    fontSize: 14,
  },
  desktopInputOutline: {
    borderRadius: 6,
  },
  desktopContinueButton: {
    borderRadius: 6,
    marginBottom: 12,
  },
  desktopContinueButtonLabel: {
    fontSize: 14,
    fontWeight: "500",
  },
});
